import { useMemo, memo } from "react";
import Chart from "react-apexcharts";
import './chart.css';

function ChartBar({
  data,
  categoryKey,
  valueKey,
  percentageKey,
  chartType = "bar",
  titleX,
  titleY,
  isHorizontal = false,
  seriesName = "Data",
  showPercentage = true,
  colorMap = null,
  labelMap = null,
}) {
  const sortedData = useMemo(
    () => data?.length ? [...data].sort((a, b) => showPercentage ? b[percentageKey] - a[percentageKey] : b[valueKey] - a[valueKey]) : [],
    [data, showPercentage, percentageKey, valueKey]
  );

  const categories = useMemo(
    () => sortedData.map((item) => labelMap?.[item[categoryKey]] ?? item[categoryKey]),
    [sortedData, categoryKey, labelMap]
  );

  const barColors = useMemo(
    () => colorMap ? sortedData.map((item) => colorMap[item[categoryKey]] ?? "#4296bd") : undefined,
    [colorMap, sortedData, categoryKey]
  );

  const series = useMemo(
    () => [{
      name: seriesName,
      data: showPercentage
        ? sortedData.map((item) => item[percentageKey])
        : sortedData.map((item) => item[valueKey]),
    }],
    [seriesName, showPercentage, sortedData, percentageKey, valueKey]
  );

  const options = useMemo(
    () => ({
      chart: { type: chartType },
      colors: barColors,
      plotOptions: {
        bar: {
          borderRadius: 0,
          borderRadiusApplication: "end",
          horizontal: isHorizontal,
          distributed: !!colorMap,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => showPercentage ? `${val.toFixed(0)}%` : val,
      },
      xaxis: {
        categories,
        title: { text: titleX, style: { color: "var(--text-50)" } },
        labels: { style: { colors: "var(--text-50)" } },
      },
      yaxis: {
        title: { text: titleY, style: { color: "var(--text-50)" } },
        labels: {
          style: { colors: "var(--text-50)" },
          formatter: (val) => showPercentage ? `${val.toFixed(0)}%` : val,
        },
      },
      tooltip: {
        y: { formatter: (val) => showPercentage ? `${val.toFixed(2)}%` : val },
      },
      legend: { labels: { colors: "var(--text-50)" } },
    }),
    [chartType, barColors, isHorizontal, colorMap, showPercentage, categories, titleX, titleY]
  );

  return (
    <Chart options={options} series={series} type={chartType} height="100%" width="100%" />
  );
}

export default memo(ChartBar);
